import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";
import HomeIcon from "@mui/icons-material/Home";
import {
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Stack,
  Typography,
  Chip,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink, useNavigate } from "react-router-dom";

// Styled Components
const BreadcrumbContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  background:
    "linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.9) 100%)",
  borderRadius: "16px",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  marginBottom: theme.spacing(3),
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1.5, 0),
    marginBottom: theme.spacing(2),
    borderRadius: "12px",
  },
}));

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  "& .MuiBreadcrumbs-separator": {
    color: theme.palette.primary.main,
    margin: theme.spacing(0, 1),
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiBreadcrumbs-separator": {
      margin: theme.spacing(0, 0.5),
    },
  },
}));

const BreadcrumbLink = styled(Link)<{ component?: any; to?: string }>(
  ({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: 500,
    textDecoration: "none",
    color: theme.palette.text.secondary,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(102, 126, 234, 0.1)",
      color: theme.palette.primary.main,
      transform: "translateY(-1px)",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "0.8rem",
      padding: theme.spacing(0.25, 0.75),
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
      gap: theme.spacing(0.25),
    },
  })
);

const BreadcrumbText = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.text.disabled,
  [theme.breakpoints.down("md")]: {
    fontSize: "0.8rem",
    padding: theme.spacing(0.25, 0.75),
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    gap: theme.spacing(0.25),
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "2rem",
  background:
    "linear-gradient(135deg, #2c3e50 0%, #3498db 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  letterSpacing: "-0.5px",
  lineHeight: 1.2,
  [theme.breakpoints.down("lg")]: {
    fontSize: "1.75rem",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "1.5rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.25rem",
  },
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  padding: "10px",
  borderRadius: "12px",
  backgroundColor: "rgba(102, 126, 234, 0.1)",
  color: theme.palette.primary.main,
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(102, 126, 234, 0.2)",
    transform: "translateX(-2px) scale(1.05)",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px",
  },
}));

const SeparatorIcon = styled(ArrowRightRoundedIcon)(({ theme }) => ({
  fontSize: "1rem",
  opacity: 0.7,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.875rem",
  },
}));

type BreadcrumbItem = {
  label: string | any;
  href?: string;
};

interface BreadcrumbProps {
  breadcrumbs: BreadcrumbItem[];
  label: string;
  href?: string;
  prevUrl?: string;
}

export default function Breadcrumb(props: BreadcrumbProps) {
  const { breadcrumbs, label, href, prevUrl } = props;
  const navigate = useNavigate();

  return (
    <Fade in timeout={600}>
      <BreadcrumbContainer>
        <Box sx={{ px: { xs: 2, md: 3 } }}>
          {/* Breadcrumb Navigation */}
          <StyledBreadcrumbs
            separator={<SeparatorIcon />}
            sx={{ mb: 2 }}
          >
            {breadcrumbs.map((item, i) => {
              if (!item.href) {
                return (
                  <BreadcrumbText key={i}>
                    {i === 0 && <HomeIcon sx={{ fontSize: "1rem" }} />}
                    {item.label}
                  </BreadcrumbText>
                );
              }

              return (
                <BreadcrumbLink
                  component={RouterLink}
                  to={item.href}
                  key={i}
                >
                  {i === 0 && <HomeIcon sx={{ fontSize: "1rem" }} />}
                  {item.label}
                </BreadcrumbLink>
              );
            })}
          </StyledBreadcrumbs>

          {/* Page Title Section */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ minHeight: "48px" }}
          >
            {(href || prevUrl) && (
              <BackButton
                size="small"
                onClick={() => {
                  if (href) {
                    navigate(href);
                  } else if (prevUrl) {
                    if (window.history && window.history.state?.idx > 0) {
                      navigate(-1);
                    } else {
                      navigate(prevUrl);
                    }
                  }
                }}
              >
                <ArrowBackIosRoundedIcon fontSize="small" />
              </BackButton>
            )}

            {label && (
              <Box flex={1}>
                <PageTitle variant="h1">{label}</PageTitle>
              </Box>
            )}
          </Stack>
        </Box>
      </BreadcrumbContainer>
    </Fade>
  );
}
